import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import usersService from "../../services/UsersService";
import {
  eventOccursOnDate,
  eventsOnDate,
  formatDuration,
  formatTime,
  sameDay,
} from "../../utils/eventDates";

// Same calendar/day-list pattern as myfit-app's Dashboard (see
// src/components/EventCalendar.tsx + src/screens/DashboardScreen.tsx
// there) — a month grid with a dot under any day that has an event, plus
// a list of the selected day's events below it. Replaces the "All NFTS
// Update" vendor-template box that used to sit here. Styled with this
// app's own brand gradient/palette (see index.css's .btn-gradient/
// .text-gradient and tailwind.config.js's pink/gold/light-green) rather
// than the flat single-color first pass.
const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

// Cycled by day-of-month / row index so the grid and day-list both read
// as more than one flat accent color.
const ACCENTS = ["#F539F8", "#F2994A", "#27AE60", "#5356FB"];

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function buildMonthGrid(monthAnchor) {
  const first = startOfMonth(monthAnchor);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const leadingBlanks = first.getDay();

  const cells = Array(leadingBlanks).fill(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(new Date(first.getFullYear(), first.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function DashboardCalendar({ className }) {
  const api = new usersService();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));

  useEffect(() => {
    api
      .fetchEvents()
      .then((res) => setEvents(res.data.events || []))
      .catch((err) => {
        setError(err?.response?.data?.error || "Could not load your events.");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cells = useMemo(() => buildMonthGrid(monthAnchor), [monthAnchor]);
  const today = new Date();
  const isToday = sameDay(today, selectedDate);
  const selectedDayEvents = useMemo(
    () => eventsOnDate(events, selectedDate),
    [events, selectedDate]
  );

  const goToMonth = (delta) => {
    setMonthAnchor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const monthLabel = monthAnchor.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  return (
    <div
      className={`update-table relative w-full p-8 bg-white dark:bg-dark-white   overflow-hidden rounded-2xl section-shadow ${
        className || ""
      }`}
    >
      <div className="absolute top-0 left-0 w-full h-1.5 btn-gradient" />

      <div className="header w-full flex justify-between items-center mb-6 mt-1">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full btn-gradient flex items-center justify-center text-white text-lg shrink-0">
            <span role="img" aria-label="Calendar">
              🗓️
            </span>
          </div>
          <h1 className="text-xl font-bold text-dark-gray dark:text-white tracking-wide">
            Calendar
          </h1>
        </div>
        <Link
          to="/events"
          className="text-sm font-bold px-4 py-2 rounded-full border-gradient"
        >
          <span className="text-gradient">View All Events →</span>
        </Link>
      </div>

      {loading ? (
        <p className="text-center text-thin-light-gray py-10">Loading…</p>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => goToMonth(-1)}
              className="w-9 h-9 rounded-full btn-gradient text-white flex items-center justify-center text-xl leading-none"
            >
              ‹
            </button>
            <span
              data-testid="calendar-month-label"
              className="text-lg font-extrabold text-gradient"
            >
              {monthLabel}
            </span>
            <button
              type="button"
              onClick={() => goToMonth(1)}
              className="w-9 h-9 rounded-full btn-gradient text-white flex items-center justify-center text-xl leading-none"
            >
              ›
            </button>
          </div>

          <div className="flex mb-2 bg-light-purple dark:bg-dark-light-purple rounded-full py-2">
            {WEEKDAY_LABELS.map((w, i) => (
              <span
                key={`${w}-${i}`}
                className="flex-1 text-center text-xs font-bold text-purple dark:text-white"
              >
                {w}
              </span>
            ))}
          </div>

          <div className="flex flex-wrap mb-6">
            {cells.map((date, i) => {
              if (!date) {
                return <div key={`blank-${i}`} className="w-[14.28%] py-1.5" />;
              }

              const isSelected = sameDay(date, selectedDate);
              const isDateToday = sameDay(date, today);
              const hasEvent = events.some((e) => eventOccursOnDate(e, date));
              const accent = ACCENTS[date.getDate() % ACCENTS.length];

              return (
                <button
                  type="button"
                  key={date.toISOString()}
                  onClick={() => setSelectedDate(date)}
                  className="w-[14.28%] py-1.5 flex flex-col items-center group"
                >
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors ${
                      isSelected
                        ? "btn-gradient text-white font-bold"
                        : isDateToday
                        ? "border-2 font-bold text-dark-gray dark:text-white"
                        : "text-dark-gray dark:text-white group-hover:bg-light-purple dark:group-hover:bg-dark-light-purple"
                    }`}
                    style={isDateToday && !isSelected ? { borderColor: "#F539F8" } : undefined}
                  >
                    {date.getDate()}
                  </span>
                  <span
                    className="w-1.5 h-1.5 rounded-full mt-1"
                    style={{
                      backgroundColor: hasEvent
                        ? isSelected
                          ? "currentColor"
                          : accent
                        : "transparent",
                      color: isSelected ? "#fff" : undefined,
                    }}
                  />
                </button>
              );
            })}
          </div>

          {error && <p className="text-center text-red-700 mb-4">{error}</p>}

          <div className="border-t border-light-purple dark:border-[#5356fb29] pt-4">
            <p className="text-sm font-bold text-dark-gray dark:text-white mb-3">
              {isToday
                ? "Today"
                : selectedDate.toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
            </p>
            {selectedDayEvents.length === 0 ? (
              <p className="text-sm text-thin-light-gray">No events today.</p>
            ) : (
              selectedDayEvents.map((e, index) => {
                const accent = ACCENTS[index % ACCENTS.length];
                return (
                  <div
                    key={e.id}
                    className="flex items-center py-3 pl-3 mb-2 rounded-xl bg-[#FAFAFA] dark:bg-[#11131F]"
                    style={{ borderLeft: `3px solid ${accent}` }}
                  >
                    <span
                      className="text-xs font-bold w-[74px] shrink-0 px-2 py-1 rounded-full text-center text-white"
                      style={{ backgroundColor: accent }}
                    >
                      {formatTime(e.date_time)}
                    </span>
                    <div className="ml-2">
                      <p className="text-sm font-semibold text-dark-gray dark:text-white">
                        {e.name}
                      </p>
                      <p className="text-xs text-thin-light-gray">
                        {formatDuration(e.duration_minutes)} · {e.family_member_name || "Self"}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
